import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { aiSuggestionsApi } from '../../utils/api/aiSuggestions';
import { usePopupHome, pageTabName, popupHomeSlot, POPUP_REFRESH } from '../../services/popupHome';   // one tab only (report c2846304)

// ── The once-a-day digest of the AI improvement suggestions (report 1492665d) ─────────────────────────────
// "Once-a-day digest popup, that takes me to a suggestions list, which AI orders in order of priority as
// either fixes or suggestions." The server makes the day's list in the morning (services/aiSuggestions.ts);
// this card says how many fixes and suggestions it holds and opens it. Only the logins ticked "Receives AI
// improvement suggestions" in Admin → Users (me.aiSuggestions) ever ask for it.
// Dressed as the "open the page this question belongs on" card (components/PopupHomeNotice), in the same
// corner, and asked in ONE browser tab (services/popupHome): the Bugify page's — where these come from and
// where an accepted one is filed — else the tab in front. Opening it, or closing it with ×, puts it down for
// the day: at once in every tab of this browser (the mirror below) and on the server for every other
// device. The next day's list brings it back.
export const AI_SUGGESTIONS_KEY = 'ai_suggestions';
export const AI_SUGGESTIONS_PATH = `/m/${AI_SUGGESTIONS_KEY}`;
const HOME = 'bugify';
const POLL_MS = 30 * 60_000;   // the list changes once a day; this only notices the morning's new one in a tab left open
const FIRST_MS = 5_000;

const seenKey = (userId: string) => `mf_aiSugSeen:${userId}`;
const seenHere = (userId: string): string => { try { return localStorage.getItem(seenKey(userId)) || ''; } catch { return ''; } };

/** That day's digest has been opened or closed by this login: down at once in every tab here, and on the
 *  server for every other device. */
export function markDigestSeen(userId: string, date: string): void {
  if (!userId || !date) return;
  try { localStorage.setItem(seenKey(userId), date); } catch { /* not shared here — the server's record still puts it down */ }
  aiSuggestionsApi.seen(date).catch(() => { /* at worst it comes up once more on another device */ });
}

/** The list opens in the one tab that answers to it, so a second press brings that tab forward. */
export const openAiSuggestions = () => { window.open(AI_SUGGESTIONS_PATH, pageTabName(AI_SUGGESTIONS_KEY)); };

type Digest = { date: string; fixes: number; suggestions: number; seen: boolean };

export default function AiSuggestionsDigest() {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const userId = me?.userId || '';
  const mine = !!userId && !!me?.aiSuggestions;
  const loc = useLocation();
  const [digest, setDigest] = useState<Digest | null>(null);
  const [refused, setRefused] = useState(false);   // unticked in Admin since this page loaded: ask no more
  const [, bump] = useState(0);

  const poll = useCallback(async () => {
    if (!userId || !localStorage.getItem('mf_token')) return;
    try {
      const d = await aiSuggestionsApi.list();
      setDigest({ date: d.date, fixes: d.fixes, suggestions: d.suggestions, seen: d.seen });
    } catch (e: any) {
      if (e?.status === 403) setRefused(true);
    }
  }, [userId]);

  useEffect(() => {
    if (!mine || refused) { setDigest(null); return; }
    const first = setTimeout(() => { void poll(); }, FIRST_MS);
    const id = setInterval(() => { void poll(); }, POLL_MS);
    // Asked again when a card is clicked in another tab, or this tab comes back (services/popupHome)…
    const again = () => { void poll(); };
    // …and put down at once when another tab of this browser opened or closed it.
    const shared = (e: StorageEvent) => { if (e.key === seenKey(userId)) bump(n => n + 1); };
    window.addEventListener(POPUP_REFRESH, again);
    window.addEventListener('storage', shared);
    return () => {
      clearTimeout(first);
      clearInterval(id);
      window.removeEventListener(POPUP_REFRESH, again);
      window.removeEventListener('storage', shared);
    };
  }, [mine, refused, poll, userId]);

  const date = digest?.date || '';
  const count = digest ? digest.fixes + digest.suggestions : 0;
  // Never over the list itself: opening the list is what puts the digest down.
  const pending = mine && !refused && !!date && count > 0 && !digest?.seen && seenHere(userId) !== date && loc.pathname !== AI_SUGGESTIONS_PATH;
  const home = usePopupHome(HOME, pending, date ? [date] : []);
  if (!pending || !digest || home === 'hidden') return null;

  const putDown = () => { markDigestSeen(userId, date); setDigest({ ...digest, seen: true }); };
  const open = () => { putDown(); openAiSuggestions(); };
  const accent = 'var(--accent)';
  const fixes = t(digest.fixes === 1 ? 'ais_one_fix' : 'ais_n_fixes', { n: digest.fixes });
  const suggestions = t(digest.suggestions === 1 ? 'ais_one_suggestion' : 'ais_n_suggestions', { n: digest.suggestions });

  // The card is one button, as PopupHomeNotice's is; the × beside it puts the digest down without opening it.
  return (
    <div aria-live="polite" style={{ position: 'fixed', right: 24, top: 76 + popupHomeSlot(HOME) * 96, zIndex: 4600, width: 320, maxWidth: 'calc(100vw - 48px)', pointerEvents: 'none' }}>
      <div style={{ position: 'relative', pointerEvents: 'auto' }}>
        <button onClick={open} title={t('ais_title')}
          style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 30px 10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderLeft: `4px solid ${accent}`, borderRadius: 12, boxShadow: 'var(--shadow-md)', animation: 'mfMenuIn 0.22s ease', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
          <span style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--c-gray-100)', color: accent }}>
            <i className="ti ti-bulb" style={{ fontSize: 16 }} aria-hidden />
          </span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('ais_title')}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{`${fixes} · ${suggestions}`}</span>
            <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary)', wordBreak: 'break-word' }}>{t('ais_digest_body')}</span>
          </span>
        </button>
        <button onClick={putDown} title={t('ais_later')} aria-label={t('ais_later')}
          style={{ position: 'absolute', top: 6, right: 6, width: 22, height: 22, padding: 0, border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />
        </button>
      </div>
    </div>
  );
}
